import { first } from '@quromedical/utils';
import { isRelay } from 'core/device';
import {
  FormCard,
  MessageCard,
  Skeleton,
  SkeletonProvider,
  Spacer,
  Visible,
} from 'design-system';
import { MARGIN } from 'design-system/theme';
import { useRelayStatuses } from 'hooks/useRelayStatuses';
import { DeviceApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';
import useSWR from 'swr';

import {
  createIssueRows,
  createNotificationRows,
  createRelayStatusRows,
} from './createRows';

interface RelayStatusProps {
  deviceId: string;
}

const deviceApi = new DeviceApi();

export const RelayStatus: React.FC<RelayStatusProps> = ({ deviceId }) => {
  const swrKey = `trpc/devices/${deviceId}`;

  const fetcher = useCallback(() => deviceApi.getDevice(deviceId), [deviceId]);
  const { data: device, error: deviceError } = useSWR(swrKey, fetcher);

  const { data, hasError, refetch, isLoading } = useRelayStatuses([deviceId]);
  const deviceData = first(data);

  const isDeviceARelay = device && isRelay(device);

  const updateData = useCallback(async () => {
    await refetch();
  }, [refetch]);

  if (!isDeviceARelay) {
    return null;
  }

  const existingError = hasError || deviceError;
  if (existingError) {
    return (
      <MessageCard
        message={strings.RelayLoadingErrorText}
        title={strings.ErrorCardTitle}
      />
    );
  }

  const notifications = createNotificationRows(deviceData);
  const relayStatus = createRelayStatusRows(deviceData);
  const issues = createIssueRows(deviceData);

  return (
    <SkeletonProvider loading={isLoading}>
      <Skeleton>
        <FormCard
          cardColor="status-warning"
          showIcons={true}
          title={strings.RelayIssuesTitle}
          rows={issues || []}
        />
      </Skeleton>
      <Visible if={issues}>
        <Spacer height={MARGIN.m} />
        <Skeleton>
          <FormCard
            onButtonPress={updateData}
            buttonVariant="flat"
            buttonIcon="refresh"
            buttonShape="round"
            title={strings.LabelRelayStatus}
            rows={relayStatus}
          />
        </Skeleton>
      </Visible>
      <Spacer height={MARGIN.m} />
      <Skeleton>
        <FormCard title={strings.LabelNotifications} rows={notifications} />
      </Skeleton>
    </SkeletonProvider>
  );
};
